import ErrorIcon from 'components/ReusableComponents/BackgroundImage/ErrorIcon'
import { Component, createRef } from 'react'
import { Mention, MentionsInput } from 'react-mentions'

import { setCaretPosition } from '../../utils/helpers'
import styles from './EditTextDialog.module.scss'
import { EmojiPickerContainer } from './EmojiPickerContainer'
import { mentionInputDisabledStyle, mentionInputStyle } from './mentionsInputStyle'
import { optOutText } from './utils/textContentStrings'

const OPT_OUT_TEXT_LENGTH = 22
const MAX_LENGTH = 950

function getTagLessText(text) {
  return text
    .replace(/@\[?[a-zA-Z]+\]\((__)?[a-zA-Z]+(__)?\)/gi, '')
    .replace(/firstName|lastName|hotelName|inviteLink/gi, '')
    .replace(/Text STOP for NO text\./gi, '')
    .trim()
}

function transformOldGuestNameFormat(text) {
  return text
    .replace(/@\[guestName\]\(__guestName__\)/g, '@[firstName](__guestName__)')
    .replace(/@\[guestLastName\]\(__guestLastName__\)/g, '@[lastName](__guestLastName__)')
}

function transformToBackendFormat(text) {
  return text
    .replace(/@\[firstName\]\(__guestName__\)/g, '@[guestName](__guestName__)')
    .replace(/@\[lastName\]\(__guestLastName__\)/g, '@[guestLastName](__guestLastName__)')
}

function getAvailableTags(baseTags, value, requiredTagIds, optionalTagIds) {
  return baseTags.filter(
    tag =>
      !value.includes(tag.id) &&
      (requiredTagIds.includes(tag.id) || optionalTagIds.includes(tag.id))
  )
}

class TextArea extends Component {
  static defaultProps = {
    previewPrefix: ''
  }

  constructor(props) {
    super(props)
    this.inputRef = createRef()
  }
  state = {
    value: '',
    caretPos: 0,
    baseTags: [...this.props.baseTags],
    usedTags: [...this.props.baseTags].filter(tag => this.props.value.includes(tag.id)),
    availableTags: getAvailableTags(
      this.props.baseTags,
      this.props.value,
      this.props.requiredTagIds,
      this.props.optionalTagIds
    ),
    currentCharCount: 0,
    segments: 1
  }

  updateSegmentsAndCharCount() {
    const transformedValue = transformOldGuestNameFormat(this.props.value)
    console.log('transformedValue', transformedValue)
    console.log('tagless text', getTagLessText(transformedValue))
    console.log('variable cost', this.getVariableCost())
    console.log('preview prefix', this.props.previewPrefix)
    const realCharCount =
      getTagLessText(transformedValue).length +
      this.getVariableCost() +
      this.props.previewPrefix?.length
    this.setState({
      realCharCount
    })
  }

  componentDidUpdate(prevProps, prevState) {
    const usedTagsChanged =
      this.state.usedTags.length !== prevState.usedTags.length ||
      this.state.usedTags.some((tag, index) => tag.id !== prevState.usedTags[index]?.id)

    if (usedTagsChanged && this.props.handleUpdateUsedTags) {
      this.props.handleUpdateUsedTags(this.state.usedTags)
    }
  }

  componentDidMount() {
    const { value } = this.props

    this.updateSegmentsAndCharCount()
    const transformedValue = transformOldGuestNameFormat(value.replace(optOutText, ''))
    const availableTags = getAvailableTags(
      this.props.baseTags,
      transformedValue,
      this.props.requiredTagIds,
      this.props.optionalTagIds
    )
    this.setState({
      value: transformedValue,
      currentCharCount: transformedValue.replace(/@\S+\)/gm, '').length,
      availableTags
    })
    this.inputRef.current.addEventListener('blur', e => {
      const caretPos = e.target.selectionStart
      this.setState({ caretPos })
    })
  }

  getTags = () => {
    const { baseTags, usedTags } = this.state
    const cleanUsedTags = usedTags.map(usedTag => usedTag.display)
    return baseTags.filter(baseTag => !cleanUsedTags.includes(baseTag.display))
  }

  handleChangeLength = length => {
    this.setState({ currentCharCount: length })
  }

  handleChange = (e, newValue, newPlainTextValue, mentions) => {
    const value = e.target.value.slice(0, MAX_LENGTH)

    const valueWithVars = value.replace(
      /\b(firstName|lastName|hotelName|checkinDate|checkoutDate|promoName)\b/g,
      match => {
        if (!value.includes(`@[${match}]`)) {
          switch (match) {
            case 'firstName':
              return '@[firstName](__guestName__)'
            case 'lastName':
              return '@[lastName](__guestLastName__)'
            default:
              return `@[${match}](__${match}__)`
          }
        }
        return match
      }
    )

    const availableTags = getAvailableTags(
      this.props.baseTags,
      valueWithVars,
      this.props.requiredTagIds,
      this.props.optionalTagIds
    )

    const cleanText = getTagLessText(newPlainTextValue)
    const cleanTextLength = cleanText.length
    this.handleChangeLength(cleanTextLength)

    // Convert to backend format before passing to parent
    const backendFormatValue = transformToBackendFormat(
      valueWithVars.slice(0, MAX_LENGTH)
    )
    this.props.handleChange(backendFormatValue)

    this.setState({
      value: valueWithVars,
      usedTags: mentions,
      availableTags
    })
  }

  handleEmojiClick = emoji => {
    const { caretPos } = this.state
    const value = this.inputRef.current.value
    const valueWithEmoji = [
      value.substring(0, caretPos),
      emoji,
      value.substring(caretPos)
    ].join('')
    const valueWithVars = valueWithEmoji
      .replace(/firstName/g, '@[firstName](__guestName__)')
      .replace(/lastName/g, '@[lastName](__guestLastName__)')
      .replace(/(hotelName|checkinDate|checkoutDate|promoName)/g, '@[$1](__$1__)')
    this.setState({ value: valueWithVars })

    // Convert to backend format before passing to parent
    const backendFormatValue = transformToBackendFormat(valueWithVars)
    this.props.handleChange(backendFormatValue, value)

    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos + emoji.length)
    }, 0)
  }

  handleEmojiOpen = () => {
    const { caretPos } = this.state
    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos)
    }, 0)
  }

  handleUnusedTagClick = tag => {
    const { caretPos } = this.state
    setCaretPosition(this.inputRef.current, caretPos)
    const value = this.inputRef.current.value

    const valueWithTag = [
      value.substring(0, caretPos),
      tag.display,
      ' ',
      value.substring(caretPos)
    ].join('')

    const valueWithVars = valueWithTag
      .replace(/firstName/g, '@[firstName](__guestName__)')
      .replace(/lastName/g, '@[lastName](__guestLastName__)')
      .replace(/(hotelName|checkinDate|checkoutDate|promoName)/g, '@[$1](__$1__)')

    const availableTags = getAvailableTags(
      this.props.baseTags,
      valueWithVars,
      this.props.requiredTagIds,
      this.props.optionalTagIds
    )

    this.setState({
      value: valueWithVars,
      availableTags,
      usedTags: [...this.state.usedTags, tag]
    })

    // Convert to backend format before passing to parent
    const backendFormatValue = transformToBackendFormat(valueWithVars)
    this.props.handleChange(backendFormatValue, value)

    setTimeout(() => {
      setCaretPosition(this.inputRef.current, caretPos + tag.display.length + 1)
    })
  }

  getVariableCost = () => {
    const optOutTextLength = this.props.shouldShowOptOutText ? OPT_OUT_TEXT_LENGTH : 0
    const cleanUsedTags = this.state.usedTags.map(usedTag => usedTag.display)
    const totalUsedTagsLength = this.state.baseTags
      .filter(baseTag => cleanUsedTags.includes(baseTag.display))
      .reduce((prev, curr) => {
        return prev + curr.length
      }, 0)

    return totalUsedTagsLength + optOutTextLength
  }

  render() {
    return (
      <>
        <div
          className={
            this.props?.disabled ? styles.textareaHolderDisabled : styles.textareaHolder
          }
          style={{ borderColor: this.props.isErred ? 'red' : '#c0b8b8' }}
        >
          <MentionsInput
            value={this.state.value}
            onChange={this.handleChange}
            disabled={this.props?.disabled}
            style={this.props?.disabled ? mentionInputDisabledStyle : mentionInputStyle}
            inputRef={this.inputRef}
            placeholder="Type your message here"
            maxLength={MAX_LENGTH}
            onFocus={this?.props?.onFocus}
            autoFocus={this?.props?.autoFocus}
          >
            <Mention
              trigger="##"
              data={this.getTags}
              className={styles.mentions}
              appendSpaceOnAdd={true}
            />
          </MentionsInput>
          {!this.props?.disabled && (
            <div className={styles.textareaFooter}>
              {this.props.hasEmojiPicker && (
                <>
                  <EmojiPickerContainer
                    onSelect={this.handleEmojiClick}
                    onOpen={this.handleEmojiOpen}
                  />
                  <div className={styles.emojiPickerSpacer} />
                </>
              )}
              {this.state.availableTags.length > 0 && (
                <div className={styles.remainingRequiredTags}>
                  <p className={styles.requiredTagsLabel}>Tags</p>
                  {this.state.availableTags.map(tag => {
                    const isRequired = this.props.requiredTagIds.includes(tag.id)

                    return (
                      <div
                        onClick={() => this.handleUnusedTagClick(tag)}
                        key={tag.id}
                        className={
                          isRequired
                            ? `${styles.requiredTag} ${styles.requiredTagMandatory}`
                            : styles.requiredTag
                        }
                      >
                        {tag.display}
                        {isRequired && (
                          <span className={styles.requiredTagDot} aria-hidden="true" />
                        )}
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        {this.state.value.match(
          /(?:(http|https):\/\/)?(?:[\w-]+\.)+[a-z]{2,6}(\S+)?$/
        ) && (
          <p className={styles.recommendation}>
            <ErrorIcon />
            Messages with short links may be marked as spam.
          </p>
        )}
      </>
    )
  }
}

TextArea.defaultProps = {
  hasEmojiPicker: true,
  value: '',
  optionalTagIds: ['__guestLastName__']
}

export default TextArea
